/**
 * Password Policy Validator
 *
 * Requirements:
 * 1. Length: 12 - 128 characters
 * 2. Uppercase: At least 1 letter (A-Z)
 * 3. Lowercase: At least 1 letter (a-z)
 * 4. Number: At least 1 digit (0-9)
 * 5. Special Character: At least 1 symbol (!@#$%^&*()_+-=[]{};':"|,.<>/?~`)
 * 6. No Personal Info: Cannot contain user's name (tokens >= 3 chars), email prefix (>= 3 chars), or phone number (last 6 digits)
 * 7. No Common Words: Cannot contain guessable words (e.g., password, welcome, hospital, admin)
 * 8. No Sequential / Repeated Runs: No 4+ runs like 1234, abcd, qwerty, aaaa
 */

export const hasSequentialOrRepeatedRuns = (password = "") => {
  if (!password || password.length < 4) return false;

  // 1. Repeated 4+ identical characters (e.g., aaaa, 1111, $$$$)
  if (/(.)\1{3,}/.test(password)) {
    return true;
  }

  const lower = password.toLowerCase();

  // 2. Sequential alphabetical (4+ ascending or descending)
  // 3. Sequential numeric (4+ ascending or descending)
  for (let i = 0; i <= lower.length - 4; i++) {
    const c0 = lower.charCodeAt(i);
    const c1 = lower.charCodeAt(i + 1);
    const c2 = lower.charCodeAt(i + 2);
    const c3 = lower.charCodeAt(i + 3);

    // a-z: 97-122
    if (c0 >= 97 && c0 <= 122 && c1 >= 97 && c1 <= 122 && c2 >= 97 && c2 <= 122 && c3 >= 97 && c3 <= 122) {
      if (c1 === c0 + 1 && c2 === c1 + 1 && c3 === c2 + 1) return true; // e.g. abcd
      if (c1 === c0 - 1 && c2 === c1 - 1 && c3 === c2 - 1) return true; // e.g. dcba
    }

    // 0-9: 48-57
    if (c0 >= 48 && c0 <= 57 && c1 >= 48 && c1 <= 57 && c2 >= 48 && c2 <= 57 && c3 >= 48 && c3 <= 57) {
      if (c1 === c0 + 1 && c2 === c1 + 1 && c3 === c2 + 1) return true; // e.g. 1234
      if (c1 === c0 - 1 && c2 === c1 - 1 && c3 === c2 - 1) return true; // e.g. 4321
    }
  }

  // 4. Keyboard sequence runs (4+ chars)
  const keyboardRows = [
    "qwertyuiop",
    "asdfghjkl",
    "zxcvbnm",
  ];

  for (const row of keyboardRows) {
    const revRow = row.split("").reverse().join("");
    for (let i = 0; i <= row.length - 4; i++) {
      const sub = row.substring(i, i + 4);
      if (lower.includes(sub)) return true;
      const revSub = revRow.substring(i, i + 4);
      if (lower.includes(revSub)) return true;
    }
  }

  return false;
};

export const hasCommonBreachedWords = (password = "") => {
  if (!password) return false;
  const lower = password.toLowerCase();
  const commonBlacklist = [
    "password",
    "welcome",
    "hospital",
    "admin",
    "doctor",
    "patient",
    "letmein",
    "changeme",
    "default",
    "qwerty",
  ];

  for (const word of commonBlacklist) {
    if (lower.includes(word)) return true;
  }
  return false;
};

export const checkPasswordRules = (
  password = "",
  userContext = {}
) => {
  const minLength = password.length >= 12 && password.length <= 128;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password);

  let noPersonalInfo = true;
  if (userContext && password.length > 0) {
    const lowerPwd = password.toLowerCase();

    // Check name tokens (>= 3 chars)
    if (userContext.name) {
      const tokens = String(userContext.name)
        .toLowerCase()
        .split(/\s+/)
        .filter((t) => t.length >= 3);
      if (tokens.some((token) => lowerPwd.includes(token))) {
        noPersonalInfo = false;
      }
    }

    // Check email prefix (>= 3 chars)
    if (userContext.email) {
      const emailPrefix = String(userContext.email).split("@")[0]?.toLowerCase();
      if (emailPrefix && emailPrefix.length >= 3 && lowerPwd.includes(emailPrefix)) {
        noPersonalInfo = false;
      }
    }

    // Check phone number (last 6 digits)
    const phone = userContext.mobile || userContext.phone;
    if (phone) {
      const cleanMobile = String(phone).replace(/\D/g, "");
      if (cleanMobile.length >= 6 && lowerPwd.includes(cleanMobile.slice(-6))) {
        noPersonalInfo = false;
      }
    }
  }

  const noRepeatedOrSequential = !hasSequentialOrRepeatedRuns(password);
  const noCommonWords = !hasCommonBreachedWords(password);
  const noCommonOrSequential = noRepeatedOrSequential && noCommonWords;

  const isValid =
    minLength &&
    hasUpper &&
    hasLower &&
    hasNumber &&
    hasSpecial &&
    noPersonalInfo &&
    noCommonOrSequential;

  return {
    minLength,
    hasUpper,
    hasLower,
    hasNumber,
    hasSpecial,
    noPersonalInfo,
    noRepeatedOrSequential,
    noCommonWords,
    noCommonOrSequential,
    isValid,
  };
};

export default checkPasswordRules;
